import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Mail, Send, Users, X, AlertCircle, CheckCircle, Search, Bold, Italic, List, Link, Type, Eye, EyeOff, BarChart2, RefreshCw, Underline, AlignLeft, AlignCenter, AlignRight, Image, Minus, ListOrdered, Quote, Code, Palette, ChevronDown, ChevronUp, History, Copy, Trash2, Clock, MousePointer, AlertTriangle, CheckSquare, FileText, Filter, ThumbsDown } from 'lucide-react';
import { supabase } from '../../../lib/supabase';
import { useAuth } from '../../../contexts/AuthContext';

import NewsletterTemplates from './NewsletterTemplates';




// ─── Toolbar config ──────────────────────────────────────────────────────────
const TOOLBAR_GROUPS = [
  {
    label: 'Text Style',
    actions: [
      { icon: Bold,          label: 'Bold',          tag: 'b',          wrap: true },
      { icon: Italic,        label: 'Italic',         tag: 'i',          wrap: true },
      { icon: Underline,     label: 'Underline',      tag: 'u',          wrap: true },
      { icon: Code,          label: 'Inline Code',    tag: 'code',       wrap: true },
    ],
  },
  {
    label: 'Headings',
    actions: [
      { icon: Type,  label: 'Heading 1', tag: 'h1', wrap: false },
      { icon: Type,  label: 'Heading 2', tag: 'h2', wrap: false },
      { icon: Type,  label: 'Heading 3', tag: 'h3', wrap: false },
    ],
  },
  {
    label: 'Alignment',
    actions: [
      { icon: AlignLeft,   label: 'Align Left',   tag: 'p-left',   wrap: false },
      { icon: AlignCenter, label: 'Align Center', tag: 'p-center', wrap: false },
      { icon: AlignRight,  label: 'Align Right',  tag: 'p-right',  wrap: false },
    ],
  },
  {
    label: 'Lists & Blocks',
    actions: [
      { icon: List,         label: 'Bullet List',    tag: 'ul',         wrap: false },
      { icon: ListOrdered,  label: 'Numbered List',  tag: 'ol',         wrap: false },
      { icon: Quote,        label: 'Blockquote',     tag: 'blockquote', wrap: false },
      { icon: Minus,        label: 'Divider',        tag: 'hr',         wrap: false },
    ],
  },
  {
    label: 'Insert',
    actions: [
      { icon: Link,   label: 'Link',        tag: 'a',   wrap: false },
      { icon: Image,  label: 'Image',       tag: 'img', wrap: false },
    ],
  },
];

// Flatten for easy lookup
const ALL_ACTIONS = TOOLBAR_GROUPS?.flatMap(g => g?.actions);

// ─── Snippet templates ────────────────────────────────────────────────────────
const SNIPPETS = [
  {
    label: 'CTA Button',
    html: `<p style="text-align:center;"><a class="cta" href="https://fortifiedmind.org" style="display:inline-block;background:#f59e0b;color:#0f0f1a;padding:12px 28px;border-radius:8px;text-decoration:none;font-weight:600;">Visit Fortified Mind</a></p>`,
  },
  {
    label: 'Greeting Paragraph',
    html: `<p>Dear Fortified Mind family,</p>\n<p>We have exciting updates to share with you this week...</p>`,
  },
  {
    label: 'Scripture Quote',
    html: `<blockquote style="border-left:4px solid #f59e0b;margin:16px 0;padding:12px 20px;background:#fef9ee;color:#1a1a2e;font-style:italic;">"Your word is a lamp to my feet and a light to my path." — Psalm 119:105</blockquote>`,
  },
  {
    label: 'Two-Column Section',
    html: `<table width="100%" cellpadding="0" cellspacing="0" style="border-collapse:collapse;">\n  <tr>\n    <td width="50%" style="padding:8px;vertical-align:top;"><p><strong>Column One</strong></p><p>Content here...</p></td>\n    <td width="50%" style="padding:8px;vertical-align:top;"><p><strong>Column Two</strong></p><p>Content here...</p></td>\n  </tr>\n</table>`,
  },
  {
    label: 'Highlight Box',
    html: `<div style="background:#fef3c7;border:1px solid #f59e0b;border-radius:8px;padding:16px 20px;margin:16px 0;">\n  <p style="margin:0;font-weight:600;color:#92400e;">📌 Important Announcement</p>\n  <p style="margin:8px 0 0;">Your message here...</p>\n</div>`,
  },
];

// ─── Color palette ────────────────────────────────────────────────────────────
const TEXT_COLORS = [
  { label: 'Default',   value: '' },
  { label: 'Gold',      value: '#f59e0b' },
  { label: 'Dark',      value: '#1a1a2e' },
  { label: 'Red',       value: '#dc2626' },
  { label: 'Green',     value: '#16a34a' },
  { label: 'Blue',      value: '#2563eb' },
  { label: 'Gray',      value: '#6b7280' },
];

// ─── Helper: display name ─────────────────────────────────────────────────────
const getDisplayName = (u) => {
  const full = [u?.first_name, u?.last_name]?.filter(Boolean)?.join(' ')?.trim();
  if (full) return full;
  if (u?.username) return u?.username;
  return u?.email?.split('@')?.[0] || 'Unknown';
};

// ─── Helper: engagement rate ──────────────────────────────────────────────────
const calcRate = (count, total) => {
  if (!total || total === 0) return null;
  return Math.round((count / total) * 100);
};

// ─── Stat pill component ──────────────────────────────────────────────────────
const StatPill = ({ icon, label, value, color, rate }) => {
  const IconComponent = icon;
  return (
    <div className={`flex items-center space-x-2 px-3 py-1.5 rounded-lg bg-${color}/10 border border-${color}/20`}>
      <IconComponent className={`w-3.5 h-3.5 text-${color}`} />
      <div>
        <span className={`text-xs font-semibold text-${color}`}>{value}</span>
        {rate !== null && rate !== undefined && (
          <span className="text-xs text-muted-foreground ml-1">({rate}%)</span>
        )}
        <p className="text-xs text-muted-foreground leading-none mt-0.5">{label}</p>
      </div>
    </div>
  );
};

// ─── Segment filter options ───────────────────────────────────────────────────
const SEGMENT_OPTIONS = [
  { id: 'all', label: 'All Members', description: 'Send to everyone' },
  { id: 'opted_in', label: 'Email Opt-ins Only', description: 'Members who opted into emails' },
  { id: 'has_streak', label: 'Active Streak', description: 'Members with an active reading streak' },
  { id: 'no_streak', label: 'No Active Streak', description: 'Members who may need encouragement' },
  { id: 'bible_kjv', label: 'KJV Preference', description: 'Members using King James Version' },
  { id: 'bible_asv', label: 'ASV Preference', description: 'Members using American Standard Version' },
  { id: 'in_reading_plan', label: 'Enrolled in Reading Plan', description: 'Members with an active reading plan' },
  { id: 'daily_frequency', label: 'Daily Frequency', description: 'Members set to daily reminders' },
  { id: 'weekly_frequency', label: 'Weekly Frequency', description: 'Members set to weekly reminders' },
  { id: 'selected', label: 'Select Individually', description: 'Manually pick recipients' },
];

// ─── Main component ───────────────────────────────────────────────────────────
const NewsletterComposer = () => {
  const { userProfile } = useAuth();

  // Composer state
  const [subject, setSubject] = useState('');
  const [htmlBody, setHtmlBody] = useState('');
  const [previewMode, setPreviewMode] = useState(false);
  const [recipientMode, setRecipientMode] = useState('all');
  const [allUsers, setAllUsers] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [userSearch, setUserSearch] = useState('');
  const [sending, setSending] = useState(false);
  const [sendResult, setSendResult] = useState(null);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [analytics, setAnalytics] = useState(null);
  const [loadingAnalytics, setLoadingAnalytics] = useState(false);
  const textareaRef = useRef(null);

  // Toolbar extras
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [showSnippets, setShowSnippets] = useState(false);

  // Saved newsletters state
  const [activeTab, setActiveTab] = useState('compose'); // 'compose' | 'history'
  const [savedNewsletters, setSavedNewsletters] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [expandedNewsletter, setExpandedNewsletter] = useState(null);
  const [expandedPreviewTab, setExpandedPreviewTab] = useState({}); // { [id]: 'preview' | 'source' }

  // Templates & segment state
  const [showTemplates, setShowTemplates] = useState(false);
  const [showSegmentFilter, setShowSegmentFilter] = useState(false);
  const [activeSegment, setActiveSegment] = useState('all');

  useEffect(() => {
    fetchUsers();
    fetchAnalytics();
  }, []);

  useEffect(() => {
    if (activeTab === 'history') fetchHistory();
  }, [activeTab]);

  // ── Data fetching ──────────────────────────────────────────────────────────
  const fetchUsers = async () => {
    setLoadingUsers(true);
    try {
      const { data, error } = await supabase?.rpc('admin_get_all_users');
      if (!error && data) setAllUsers(data);
    } catch (err) {
      console.error('Error fetching users:', err);
    } finally {
      setLoadingUsers(false);
    }
  };

  const fetchAnalytics = async () => {
    setLoadingAnalytics(true);
    try {
      const { data: profileData } = await supabase
        ?.from('user_profiles')
        ?.select('notification_preferences, created_at')
        ?.not('email', 'is', null);

      if (profileData) {
        const total = profileData?.length;
        const optedIn = profileData?.filter(p => {
          const prefs = p?.notification_preferences;
          return prefs?.planEmails !== false && prefs?.dailyReminders !== false;
        })?.length;
        const now = new Date();
        const thisMonth = profileData?.filter(p => {
          const d = new Date(p.created_at);
          return d?.getMonth() === now?.getMonth() && d?.getFullYear() === now?.getFullYear();
        })?.length;
        setAnalytics({ total, optedIn, thisMonth });
      }
    } catch (err) {
      console.error('Error fetching analytics:', err);
    } finally {
      setLoadingAnalytics(false);
    }
  };

  const fetchHistory = async () => {
    setLoadingHistory(true);
    try {
      const { data, error } = await supabase
        ?.from('sent_newsletters')
        ?.select('*')
        ?.order('sent_at', { ascending: false })
        ?.limit(50);
      if (!error && data) setSavedNewsletters(data);
    } catch (err) {
      console.error('Error fetching newsletter history:', err);
    } finally {
      setLoadingHistory(false);
    }
  };

  // ── Segment filtering ──────────────────────────────────────────────────────
  const getSegmentedUsers = () => {
    switch (activeSegment) {
      case 'opted_in':
        return allUsers?.filter(u => {
          const prefs = u?.notification_preferences;
          return prefs?.planEmails !== false && prefs?.dailyReminders !== false;
        });
      case 'has_streak':
        return allUsers?.filter(u => (u?.current_streak || 0) > 0);
      case 'no_streak':
        return allUsers?.filter(u => !u?.current_streak || u?.current_streak === 0);
      case 'bible_kjv':
        return allUsers?.filter(u => {
          const v = (u?.preferred_bible_version || '')?.toLowerCase();
          return v === 'kjv' || v === '' || !v;
        });
      case 'bible_asv':
        return allUsers?.filter(u => {
          const v = (u?.preferred_bible_version || '')?.toLowerCase();
          return v === 'asv';
        });
      case 'in_reading_plan':
        return allUsers?.filter(u => u?.active_plan_id || u?.has_reading_plan);
      case 'daily_frequency':
        return allUsers?.filter(u => {
          const prefs = u?.notification_preferences;
          return prefs?.frequency === 'daily' || !prefs?.frequency;
        });
      case 'weekly_frequency':
        return allUsers?.filter(u => {
          const prefs = u?.notification_preferences;
          return prefs?.frequency === 'weekly';
        });
      case 'selected':
        return allUsers?.filter(u => selectedUsers?.includes(u?.id));
      default:
        return allUsers;
    }
  };

  // ── Filtering ──────────────────────────────────────────────────────────────
  const baseUsers = activeSegment === 'selected' ? allUsers : getSegmentedUsers();
  const filteredUsers = baseUsers?.filter(u => {
    if (!userSearch) return true;
    const q = userSearch?.toLowerCase();
    const displayName = getDisplayName(u)?.toLowerCase();
    return (
      u?.email?.toLowerCase()?.includes(q) ||
      displayName?.includes(q) ||
      u?.username?.toLowerCase()?.includes(q)
    );
  });

  const toggleUserSelection = (userId) => {
    setSelectedUsers(prev =>
      prev?.includes(userId) ? prev?.filter(id => id !== userId) : [...prev, userId]
    );
  };

  // ── Template selection ─────────────────────────────────────────────────────
  const handleTemplateSelect = (template) => {
    setSubject(template?.subject || '');
    setHtmlBody(template?.body || '');
    setShowTemplates(false);
  };

  // ── Formatting insertion ───────────────────────────────────────────────────
  const insertFormatting = useCallback((tag) => {
    const textarea = textareaRef?.current;
    if (!textarea) return;
    const start = textarea?.selectionStart;
    const end = textarea?.selectionEnd;
    const selected = htmlBody?.substring(start, end);
    let insertion = '';

    switch (tag) {
      case 'ul':
        insertion = `\n<ul>\n  <li>${selected || 'List item'}</li>\n  <li>Another item</li>\n</ul>\n`;
        break;
      case 'ol':
        insertion = `\n<ol>\n  <li>${selected || 'First item'}</li>\n  <li>Second item</li>\n</ol>\n`;
        break;
      case 'a':
        insertion = `<a href="https://fortifiedmind.org" style="color:#f59e0b;">${selected || 'Link text'}</a>`;
        break;
      case 'img':
        insertion = `<img src="https://your-image-url.jpg" alt="Image description" style="max-width:100%;border-radius:8px;margin:12px 0;" />`;
        break;
      case 'h1':
        insertion = `\n<h1 style="color:#f59e0b;font-size:28px;margin:24px 0 12px;">${selected || 'Main Heading'}</h1>\n`;
        break;
      case 'h2':
        insertion = `\n<h2 style="color:#d97706;font-size:22px;margin:20px 0 10px;">${selected || 'Section Heading'}</h2>\n`;
        break;
      case 'h3':
        insertion = `\n<h3 style="color:#b45309;font-size:18px;margin:16px 0 8px;">${selected || 'Sub-heading'}</h3>\n`;
        break;
      case 'blockquote':
        insertion = `\n<blockquote style="border-left:4px solid #f59e0b;margin:16px 0;padding:12px 20px;background:#fef9ee;color:#1a1a2e;font-style:italic;">${selected || 'Quote text here'}</blockquote>\n`;
        break;
      case 'hr':
        insertion = `\n<hr style="border:none;border-top:1px solid #e5e7eb;margin:24px 0;" />\n`;
        break;
      case 'code':
        insertion = `<code style="background:#f3f4f6;padding:2px 6px;border-radius:4px;font-family:monospace;font-size:14px;">${selected || 'code'}</code>`;
        break;
      case 'p-left':
        insertion = `\n<p style="text-align:left;">${selected || 'Left aligned text'}</p>\n`;
        break;
      case 'p-center':
        insertion = `\n<p style="text-align:center;">${selected || 'Centered text'}</p>\n`;
        break;
      case 'p-right':
        insertion = `\n<p style="text-align:right;">${selected || 'Right aligned text'}</p>\n`;
        break;
      default:
        insertion = `<${tag}>${selected}</${tag}>`;
    }

    const newBody = htmlBody?.substring(0, start) + insertion + htmlBody?.substring(end);
    setHtmlBody(newBody);
    setTimeout(() => {
      textarea?.focus();
      textarea?.setSelectionRange(start + insertion?.length, start + insertion?.length);
    }, 0);
  }, [htmlBody]);

  const insertColor = useCallback((color) => {
    const textarea = textareaRef?.current;
    if (!textarea) return;
    const start = textarea?.selectionStart;
    const end = textarea?.selectionEnd;
    const selected = htmlBody?.substring(start, end) || 'Colored text';
    let insertion = color
      ? `<span style="color:${color};">${selected}</span>`
      : selected;
    const newBody = htmlBody?.substring(0, start) + insertion + htmlBody?.substring(end);
    setHtmlBody(newBody);
    setShowColorPicker(false);
    setTimeout(() => textarea?.focus(), 0);
  }, [htmlBody]);

  const insertSnippet = useCallback((snippetHtml) => {
    const textarea = textareaRef?.current;
    if (!textarea) return;
    const pos = textarea?.selectionStart;
    const newBody = htmlBody?.substring(0, pos) + '\n' + snippetHtml + '\n' + htmlBody?.substring(pos);
    setHtmlBody(newBody);
    setShowSnippets(false);
    setTimeout(() => textarea?.focus(), 0);
  }, [htmlBody]);

  // ── Recipients ─────────────────────────────────────────────────────────────
  const getRecipientEmails = () => {
    const segmented = getSegmentedUsers();
    if (activeSegment === 'selected') {
      return allUsers?.filter(u => selectedUsers?.includes(u?.id))?.map(u => u?.email)?.filter(Boolean);
    }
    return segmented?.map(u => u?.email)?.filter(Boolean);
  };

  // ── Send ───────────────────────────────────────────────────────────────────
  const handleSend = async () => {
    if (!subject?.trim()) { setSendResult({ success: false, message: 'Please enter a subject.' }); return; }
    if (!htmlBody?.trim()) { setSendResult({ success: false, message: 'Please enter email body content.' }); return; }
    const emails = getRecipientEmails();
    if (emails?.length === 0) { setSendResult({ success: false, message: 'No recipients selected.' }); return; }

    setSending(true);
    setSendResult(null);

    try {
      // Save to history first to get the ID for tracking
      const { data: savedRecord, error: saveError } = await supabase
        ?.from('sent_newsletters')
        ?.insert({
          subject,
          html_body: htmlBody,
          recipient_mode: activeSegment,
          recipient_count: emails?.length,
          sent_by: userProfile?.id || null,
        })
        ?.select('id')
        ?.single();

      if (saveError) throw saveError;

      const { data, error } = await supabase?.functions?.invoke('send-newsletter', {
        body: {
          subject,
          htmlBody: buildEmailHtml(subject, htmlBody),
          recipients: emails,
          newsletterId: savedRecord?.id,
        },
      });

      if (error) throw error;

      setSendResult({
        success: true,
        message: `Newsletter sent via BCC to ${emails?.length} recipient${emails?.length !== 1 ? 's' : ''}. Recipients cannot see each other.`,
        stats: { sent: emails?.length, failed: data?.failed || 0 },
      });

      setSubject('');
      setHtmlBody('');
      setSelectedUsers([]);
      setActiveSegment('all');
    } catch (err) {
      setSendResult({ success: false, message: err?.message || 'Failed to send newsletter. Please try again.' });
    } finally {
      setSending(false);
    }
  };

  // ── Copy from history ──────────────────────────────────────────────────────
  const copyFromHistory = (newsletter) => {
    setSubject(newsletter?.subject ? `Copy of ${newsletter?.subject}` : '');
    setHtmlBody(newsletter?.html_body || '');
    setActiveTab('compose');
    setSendResult(null);
  };

  // ── Delete from history ────────────────────────────────────────────────────
  const deleteFromHistory = async (id) => {
    try {
      await supabase?.from('sent_newsletters')?.delete()?.eq('id', id);
      setSavedNewsletters(prev => prev?.filter(n => n?.id !== id));
    } catch (err) {
      console.error('Error deleting newsletter:', err);
    }
  };

  // ── Email HTML builder ─────────────────────────────────────────────────────
  const buildEmailHtml = (subj, body) => `
<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>${subj}</title>
  <style>
    body { font-family: 'Georgia', serif; background: #f8f7f4; margin: 0; padding: 20px; }
    .container { max-width: 600px; margin: 0 auto; background: #ffffff; border-radius: 12px; overflow: hidden; box-shadow: 0 4px 20px rgba(0,0,0,0.1); }
    .header { background: linear-gradient(135deg, #1a1a2e 0%, #2a2a3e 100%); padding: 32px 40px; text-align: center; }
    .header h1 { color: #f59e0b; font-size: 24px; margin: 0 0 8px; }
    .header p { color: #9090a8; font-size: 14px; margin: 0; }
    .body { padding: 40px; color: #1a1a2e; line-height: 1.7; font-size: 16px; }
    .body h1 { color: #f59e0b; font-size: 28px; margin-top: 24px; }
    .body h2 { color: #d97706; font-size: 22px; margin-top: 24px; }
    .body h3 { color: #b45309; font-size: 18px; margin-top: 20px; }
    .body a { color: #d97706; }
    .body ul, .body ol { padding-left: 20px; }
    .body blockquote { border-left: 4px solid #f59e0b; margin: 16px 0; padding: 12px 20px; background: #fef9ee; font-style: italic; }
    .body hr { border: none; border-top: 1px solid #e5e7eb; margin: 24px 0; }
    .body code { background: #f3f4f6; padding: 2px 6px; border-radius: 4px; font-family: monospace; font-size: 14px; }
    .footer { background: #f0ede8; padding: 24px 40px; text-align: center; font-size: 12px; color: #6b6b80; }
    .footer a { color: #d97706; }
    .cta { display: inline-block; background: #f59e0b; color: #0f0f1a; padding: 12px 28px; border-radius: 8px; text-decoration: none; font-weight: 600; margin: 16px 0; }
  </style>
</head>
<body>
  <div class="container">
    <div class="header">
      <h1>⚔️ Fortified Mind</h1>
      <p>Strengthening Your Scripture Memory</p>
    </div>
    <div class="body">
      ${body}
    </div>
    <div class="footer">
      <p>You're receiving this because you're a member of <a href="https://fortifiedmind.org">Fortified Mind</a>.</p>
      <p>© ${new Date()?.getFullYear()} Fortified Mind. All rights reserved.</p>
      <p><a href="https://fortifiedmind.org/privacy-policy">Privacy Policy</a> · <a href="https://fortifiedmind.org/terms-of-service">Terms of Service</a></p>
      <p>Questions? <a href="https://fortifiedmind.org/contact-us">Contact Us</a></p>
    </div>
  </div>
</body>
</html>`;

  const segmentedUsers = getSegmentedUsers();
  const recipientCount = activeSegment === 'selected' ? selectedUsers?.length : segmentedUsers?.length;
  const activeSegmentLabel = SEGMENT_OPTIONS?.find(s => s?.id === activeSegment)?.label || 'All Members';

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div className="space-y-6">
      {/* Templates modal */}
      {showTemplates && (
        <NewsletterTemplates
          onSelect={handleTemplateSelect}
          onClose={() => setShowTemplates(false)}
        />
      )}
      {/* Analytics Bar */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-card border border-border rounded-xl p-4 flex items-center space-x-3">
          <div className="bg-primary/10 p-2.5 rounded-full">
            <Users className="w-5 h-5 text-primary" />
          </div>
          <div>
            <p className="text-xs text-muted-foreground">Total Members</p>
            <p className="text-xl font-bold text-foreground">{loadingAnalytics ? '—' : analytics?.total ?? allUsers?.length}</p>
          </div>
        </div>
        <div className="bg-card border border-border rounded-xl p-4 flex items-center space-x-3">
          <div className="bg-success/10 p-2.5 rounded-full">
            <Mail className="w-5 h-5 text-success" />
          </div>
          <div>
            <p className="text-xs text-muted-foreground">Email Opt-ins</p>
            <p className="text-xl font-bold text-foreground">{loadingAnalytics ? '—' : analytics?.optedIn ?? '—'}</p>
          </div>
        </div>
        <div className="bg-card border border-border rounded-xl p-4 flex items-center space-x-3">
          <div className="bg-accent/10 p-2.5 rounded-full">
            <BarChart2 className="w-5 h-5 text-accent" />
          </div>
          <div>
            <p className="text-xs text-muted-foreground">New This Month</p>
            <p className="text-xl font-bold text-foreground">{loadingAnalytics ? '—' : analytics?.thisMonth ?? '—'}</p>
          </div>
          <button onClick={fetchAnalytics} className="ml-auto text-muted-foreground hover:text-foreground transition-colors" title="Refresh">
            <RefreshCw className="w-4 h-4" />
          </button>
        </div>
      </div>
      {/* Tab switcher */}
      <div className="flex space-x-1 bg-muted/40 border border-border rounded-xl p-1 w-fit">
        <button
          onClick={() => setActiveTab('compose')}
          className={`flex items-center space-x-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${activeTab === 'compose' ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}
        >
          <Mail className="w-4 h-4" />
          <span>Compose</span>
        </button>
        <button
          onClick={() => setActiveTab('history')}
          className={`flex items-center space-x-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${activeTab === 'history' ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}
        >
          <History className="w-4 h-4" />
          <span>Sent History</span>
          {savedNewsletters?.length > 0 && (
            <span className="bg-primary/20 text-primary text-xs px-1.5 py-0.5 rounded-full">{savedNewsletters?.length}</span>
          )}
        </button>
      </div>
      {/* ── COMPOSE TAB ── */}
      {activeTab === 'compose' && (
        <div className="bg-card border border-border rounded-2xl shadow-warm-lg p-6">
          <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
            <div className="flex items-center space-x-3">
              <div className="h-10 w-10 bg-primary/10 rounded-full flex items-center justify-center">
                <Mail className="w-5 h-5 text-primary" />
              </div>
              <div>
                <h2 className="text-xl font-heading font-bold text-foreground">Compose Newsletter</h2>
                <p className="text-muted-foreground text-sm">Write and send emails to your members</p>
              </div>
            </div>
            {/* Templates button */}
            <button
              onClick={() => setShowTemplates(true)}
              className="flex items-center space-x-2 px-4 py-2 bg-primary/10 text-primary hover:bg-primary/20 rounded-lg text-sm font-medium transition-colors border border-primary/20"
            >
              <FileText className="w-4 h-4" />
              <span>Use Template</span>
            </button>
          </div>

          {/* BCC notice */}
          <div className="mb-4 p-3 bg-primary/5 border border-primary/20 rounded-lg flex items-start space-x-2">
            <CheckSquare className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
            <p className="text-xs text-primary">Newsletters are sent via <strong>BCC</strong> — recipients cannot see each other's email addresses.</p>
          </div>

          {/* Send Result */}
          {sendResult && (
            <div className={`mb-4 p-4 rounded-lg border flex items-start space-x-3 ${sendResult?.success ? 'bg-success/10 border-success/30' : 'bg-destructive/10 border-destructive/30'}`}>
              {sendResult?.success
                ? <CheckCircle className="w-5 h-5 text-success flex-shrink-0 mt-0.5" />
                : <AlertCircle className="w-5 h-5 text-destructive flex-shrink-0 mt-0.5" />}
              <div>
                <p className={`text-sm font-medium ${sendResult?.success ? 'text-success' : 'text-destructive'}`}>{sendResult?.message}</p>
                {sendResult?.stats && (
                  <p className="text-xs text-muted-foreground mt-1">Sent: {sendResult?.stats?.sent} · Failed: {sendResult?.stats?.failed}</p>
                )}
              </div>
              <button onClick={() => setSendResult(null)} className="ml-auto text-muted-foreground hover:text-foreground">
                <X className="w-4 h-4" />
              </button>
            </div>
          )}

          {/* Subject */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-foreground mb-1.5">Subject Line</label>
            <input
              type="text"
              value={subject}
              onChange={(e) => setSubject(e?.target?.value)}
              placeholder="Enter email subject..."
              className="w-full px-3 py-2.5 border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary text-sm"
            />
          </div>

          {/* Rich Text Toolbar */}
          <div className="mb-2">
            <div className="flex items-center justify-between mb-1.5">
              <label className="block text-sm font-medium text-foreground">Email Body (HTML supported)</label>
              <button
                onClick={() => setPreviewMode(!previewMode)}
                className="flex items-center space-x-1.5 text-xs text-primary hover:text-primary/80 transition-colors"
              >
                {previewMode ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                <span>{previewMode ? 'Edit' : 'Preview'}</span>
              </button>
            </div>

            {!previewMode && (
              <div className="border border-border rounded-lg overflow-hidden mb-2">
                {/* Toolbar rows */}
                <div className="bg-muted/40 border-b border-border p-2 flex flex-wrap gap-1 items-center">
                  {/* Text style group */}
                  {TOOLBAR_GROUPS?.[0]?.actions?.map(action => (
                    <button
                      key={action?.tag}
                      onClick={() => insertFormatting(action?.tag)}
                      title={action?.label}
                      className="p-1.5 rounded hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
                    >
                      <action.icon className="w-4 h-4" />
                    </button>
                  ))}
                  <div className="w-px h-5 bg-border mx-1" />
                  {TOOLBAR_GROUPS?.[1]?.actions?.map((action, i) => (
                    <button
                      key={action?.tag}
                      onClick={() => insertFormatting(action?.tag)}
                      title={action?.label}
                      className="px-2 py-1 rounded hover:bg-muted text-muted-foreground hover:text-foreground transition-colors text-xs font-bold"
                    >
                      H{i + 1}
                    </button>
                  ))}
                  <div className="w-px h-5 bg-border mx-1" />
                  {TOOLBAR_GROUPS?.[2]?.actions?.map(action => (
                    <button
                      key={action?.tag}
                      onClick={() => insertFormatting(action?.tag)}
                      title={action?.label}
                      className="p-1.5 rounded hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
                    >
                      <action.icon className="w-4 h-4" />
                    </button>
                  ))}
                  <div className="w-px h-5 bg-border mx-1" />
                  {TOOLBAR_GROUPS?.[3]?.actions?.map(action => (
                    <button
                      key={action?.tag}
                      onClick={() => insertFormatting(action?.tag)}
                      title={action?.label}
                      className="p-1.5 rounded hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
                    >
                      <action.icon className="w-4 h-4" />
                    </button>
                  ))}
                  <div className="w-px h-5 bg-border mx-1" />
                  {TOOLBAR_GROUPS?.[4]?.actions?.map(action => (
                    <button
                      key={action?.tag}
                      onClick={() => insertFormatting(action?.tag)}
                      title={action?.label}
                      className="p-1.5 rounded hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
                    >
                      <action.icon className="w-4 h-4" />
                    </button>
                  ))}
                  <div className="w-px h-5 bg-border mx-1" />
                  {/* Color picker */}
                  <div className="relative">
                    <button
                      onClick={() => { setShowColorPicker(!showColorPicker); setShowSnippets(false); }}
                      title="Text Color"
                      className="p-1.5 rounded hover:bg-muted text-muted-foreground hover:text-foreground transition-colors flex items-center space-x-1"
                    >
                      <Palette className="w-4 h-4" />
                      <ChevronDown className="w-3 h-3" />
                    </button>
                    {showColorPicker && (
                      <div className="absolute top-full left-0 mt-1 bg-card border border-border rounded-lg shadow-lg p-2 z-20 flex flex-wrap gap-1.5 w-40">
                        {TEXT_COLORS?.map(c => (
                          <button
                            key={c?.value || 'default'}
                            onClick={() => insertColor(c?.value)}
                            title={c?.label}
                            className="w-6 h-6 rounded border border-border hover:scale-110 transition-transform"
                            style={{ background: c?.value || 'transparent' }}
                          >
                            {!c?.value && <span className="text-xs text-muted-foreground">A</span>}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                  {/* Snippets */}
                  <div className="relative">
                    <button
                      onClick={() => { setShowSnippets(!showSnippets); setShowColorPicker(false); }}
                      title="Insert Snippet"
                      className="flex items-center space-x-1 px-2 py-1.5 rounded hover:bg-muted text-muted-foreground hover:text-foreground transition-colors text-xs font-medium"
                    >
                      <span>Snippets</span>
                      <ChevronDown className="w-3 h-3" />
                    </button>
                    {showSnippets && (
                      <div className="absolute top-full left-0 mt-1 bg-card border border-border rounded-lg shadow-lg z-20 w-52">
                        {SNIPPETS?.map(s => (
                          <button
                            key={s?.label}
                            onClick={() => insertSnippet(s?.html)}
                            className="w-full text-left px-3 py-2 text-sm text-foreground hover:bg-muted transition-colors border-b border-border/50 last:border-0"
                          >
                            {s?.label}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                <textarea
                  ref={textareaRef}
                  value={htmlBody}
                  onChange={(e) => setHtmlBody(e?.target?.value)}
                  placeholder={`Write your newsletter content here. HTML is supported.\n\nExample:\n<p>Dear Fortified Mind family,</p>\n<p>We have exciting updates to share...</p>`}
                  rows={14}
                  className="w-full px-3 py-2.5 bg-background text-foreground focus:outline-none text-sm font-mono resize-y"
                />
              </div>
            )}
          </div>

          {previewMode && (
            <div
              className="min-h-[280px] border border-border rounded-lg p-4 bg-background text-foreground text-sm overflow-auto prose prose-sm max-w-none mb-4"
              dangerouslySetInnerHTML={{ __html: htmlBody || '<p class="text-muted-foreground">Nothing to preview yet...</p>' }}
            />
          )}

          {/* Recipients & Segment Filter */}
          <div className="mt-4">
            <div className="flex items-center justify-between mb-3">
              <label className="block text-sm font-medium text-foreground">Recipients</label>
              <button
                onClick={() => setShowSegmentFilter(!showSegmentFilter)}
                className={`flex items-center space-x-1.5 text-xs px-3 py-1.5 rounded-lg border transition-colors ${showSegmentFilter ? 'bg-primary text-primary-foreground border-primary' : 'bg-background border-border text-muted-foreground hover:border-primary/40 hover:text-foreground'}`}
              >
                <Filter className="w-3.5 h-3.5" />
                <span>Segment Filter</span>
                {activeSegment !== 'all' && (
                  <span className="bg-primary-foreground/20 text-primary-foreground px-1 rounded text-xs">Active</span>
                )}
              </button>
            </div>

            {/* Segment filter panel */}
            {showSegmentFilter && (
              <div className="mb-4 border border-border rounded-xl overflow-hidden">
                <div className="p-3 bg-muted/30 border-b border-border">
                  <p className="text-xs font-medium text-foreground">Filter recipients by segment</p>
                  <p className="text-xs text-muted-foreground mt-0.5">Select a segment to target a specific group of members</p>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-1 p-2">
                  {SEGMENT_OPTIONS?.map(seg => (
                    <button
                      key={seg?.id}
                      onClick={() => { setActiveSegment(seg?.id); if (seg?.id !== 'selected') setSelectedUsers([]); }}
                      className={`flex items-start space-x-2 p-2.5 rounded-lg text-left transition-all ${activeSegment === seg?.id ? 'bg-primary text-primary-foreground' : 'hover:bg-muted text-foreground'}`}
                    >
                      <div className={`w-3.5 h-3.5 rounded-full border-2 mt-0.5 shrink-0 ${activeSegment === seg?.id ? 'border-primary-foreground bg-primary-foreground' : 'border-muted-foreground'}`} />
                      <div>
                        <p className={`text-xs font-medium ${activeSegment === seg?.id ? 'text-primary-foreground' : 'text-foreground'}`}>{seg?.label}</p>
                        <p className={`text-xs ${activeSegment === seg?.id ? 'text-primary-foreground/70' : 'text-muted-foreground'}`}>{seg?.description}</p>
                      </div>
                    </button>
                  ))}
                </div>
                {activeSegment !== 'all' && activeSegment !== 'selected' && (
                  <div className="px-3 pb-3">
                    <div className="bg-primary/5 border border-primary/20 rounded-lg px-3 py-2 flex items-center justify-between">
                      <span className="text-xs text-primary font-medium">
                        Segment: <strong>{activeSegmentLabel}</strong> — {segmentedUsers?.length} recipient{segmentedUsers?.length !== 1 ? 's' : ''}
                      </span>
                      <button onClick={() => setActiveSegment('all')} className="text-xs text-muted-foreground hover:text-destructive transition-colors ml-2">
                        Clear
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Member search (for selected mode) */}
            {activeSegment === 'selected' && (
              <div className="border border-border rounded-lg overflow-hidden">
                <div className="p-3 bg-muted/30 border-b border-border">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <input
                      type="text"
                      value={userSearch}
                      onChange={(e) => setUserSearch(e?.target?.value)}
                      placeholder="Search by name, username, or email..."
                      className="w-full pl-9 pr-3 py-2 bg-background border border-border rounded-lg text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                    />
                  </div>
                </div>
                <div className="max-h-52 overflow-y-auto">
                  {loadingUsers ? (
                    <div className="p-4 text-center text-muted-foreground text-sm">Loading members...</div>
                  ) : filteredUsers?.length === 0 ? (
                    <div className="p-4 text-center text-muted-foreground text-sm">No members found</div>
                  ) : (
                    filteredUsers?.map(u => {
                      const displayName = getDisplayName(u);
                      const hasFullName = (u?.first_name || u?.last_name);
                      return (
                        <label key={u?.id} className="flex items-center space-x-3 px-4 py-2.5 hover:bg-muted/40 cursor-pointer border-b border-border/50 last:border-0">
                          <input
                            type="checkbox"
                            checked={selectedUsers?.includes(u?.id)}
                            onChange={() => toggleUserSelection(u?.id)}
                            className="rounded border-border text-primary focus:ring-primary"
                          />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center space-x-2">
                              <p className="text-sm font-medium text-foreground truncate">{displayName}</p>
                              {u?.username && hasFullName && (
                                <span className="text-xs text-muted-foreground bg-muted px-1.5 py-0.5 rounded shrink-0">@{u?.username}</span>
                              )}
                            </div>
                            <p className="text-xs text-muted-foreground truncate">{u?.email}</p>
                          </div>
                        </label>
                      );
                    })
                  )}
                </div>
                {selectedUsers?.length > 0 && (
                  <div className="p-3 bg-primary/5 border-t border-border flex items-center justify-between">
                    <span className="text-sm text-primary font-medium">{selectedUsers?.length} member{selectedUsers?.length !== 1 ? 's' : ''} selected</span>
                    <button onClick={() => setSelectedUsers([])} className="text-xs text-muted-foreground hover:text-destructive transition-colors">Clear all</button>
                  </div>
                )}
              </div>
            )}

            {/* Segment summary for non-selected modes */}
            {activeSegment !== 'selected' && (
              <div className="mt-2 p-3 bg-muted/20 border border-border rounded-lg flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <Users className="w-4 h-4 text-muted-foreground" />
                  <span className="text-sm text-foreground">
                    <strong>{activeSegmentLabel}</strong>
                    {loadingUsers ? ' — loading...' : ` — ${segmentedUsers?.length} recipient${segmentedUsers?.length !== 1 ? 's' : ''}`}
                  </span>
                </div>
                {activeSegment !== 'all' && (
                  <button onClick={() => setActiveSegment('all')} className="text-xs text-muted-foreground hover:text-destructive transition-colors">
                    Reset
                  </button>
                )}
              </div>
            )}
          </div>

          {/* Send Button */}
          <div className="mt-6 flex items-center justify-between flex-wrap gap-3">
            <div className="text-sm text-muted-foreground">
              {recipientCount > 0 ? (
                <span>Will send to <strong className="text-foreground">{recipientCount}</strong> recipient{recipientCount !== 1 ? 's' : ''} via BCC</span>
              ) : (
                <span className="text-warning">No recipients selected</span>
              )}
            </div>
            <button
              onClick={handleSend}
              disabled={sending || !subject?.trim() || !htmlBody?.trim() || recipientCount === 0}
              className="flex items-center space-x-2 px-6 py-2.5 bg-primary text-primary-foreground rounded-lg font-semibold text-sm hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed shadow-md"
            >
              {sending ? (
                <>
                  <div className="animate-spin h-4 w-4 border-2 border-primary-foreground border-t-transparent rounded-full" />
                  <span>Sending...</span>
                </>
              ) : (
                <>
                  <Send className="w-4 h-4" />
                  <span>Send Newsletter</span>
                </>
              )}
            </button>
          </div>
        </div>
      )}
      {/* ── HISTORY TAB ── */}
      {activeTab === 'history' && (
        <div className="bg-card border border-border rounded-2xl shadow-warm-lg p-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-3">
              <div className="h-10 w-10 bg-primary/10 rounded-full flex items-center justify-center">
                <History className="w-5 h-5 text-primary" />
              </div>
              <div>
                <h2 className="text-xl font-heading font-bold text-foreground">Sent Newsletters</h2>
                <p className="text-muted-foreground text-sm">Review engagement stats and copy previously sent newsletters</p>
              </div>
            </div>
            <button
              onClick={fetchHistory}
              className="p-2 text-muted-foreground hover:text-foreground transition-colors"
              title="Refresh"
            >
              <RefreshCw className="w-4 h-4" />
            </button>
          </div>

          {/* Tracking info banner */}
          <div className="mb-5 p-3 bg-muted/30 border border-border rounded-lg flex items-start space-x-2">
            <BarChart2 className="w-4 h-4 text-muted-foreground flex-shrink-0 mt-0.5" />
            <p className="text-xs text-muted-foreground">
              Open, click, bounce, and complaint rates are tracked via Resend webhook events and update automatically. Stats may take a few minutes to appear after sending.
            </p>
          </div>

          {loadingHistory ? (
            <div className="space-y-3">
              {[1, 2, 3]?.map(i => (
                <div key={i} className="h-24 bg-muted/40 rounded-lg animate-pulse" />
              ))}
            </div>
          ) : savedNewsletters?.length === 0 ? (
            <div className="text-center py-16 text-muted-foreground">
              <History className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p className="font-medium">No newsletters sent yet</p>
              <p className="text-sm mt-1">Sent newsletters will appear here for review and copying</p>
            </div>
          ) : (
            <div className="space-y-3">
              {savedNewsletters?.map(newsletter => {
                const total = newsletter?.recipient_count || 0;
                const openRate = calcRate(newsletter?.open_count, total);
                const clickRate = calcRate(newsletter?.click_count, total);
                const bounceRate = calcRate(newsletter?.bounce_count, total);
                const deliveryRate = calcRate(newsletter?.delivery_count, total);
                const complaintRate = calcRate(newsletter?.complaint_count, total);
                const hasTracking = newsletter?.resend_email_id;
                const currentPreviewTab = expandedPreviewTab?.[newsletter?.id] || 'preview';

                return (
                  <div key={newsletter?.id} className="border border-border rounded-xl overflow-hidden">
                    {/* Header row */}
                    <div className="flex items-start justify-between p-4 bg-muted/20 hover:bg-muted/30 transition-colors">
                      <div className="flex-1 min-w-0 mr-4">
                        <p className="font-semibold text-foreground truncate">{newsletter?.subject}</p>
                        <div className="flex items-center flex-wrap gap-x-3 gap-y-1 mt-1">
                          <span className="flex items-center space-x-1 text-xs text-muted-foreground">
                            <Clock className="w-3 h-3" />
                            <span>{new Date(newsletter?.sent_at)?.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>
                          </span>
                          <span className="text-xs text-muted-foreground">·</span>
                          <span className="text-xs text-muted-foreground">
                            {total} recipient{total !== 1 ? 's' : ''} ({newsletter?.recipient_mode === 'all' ? 'All members' : newsletter?.recipient_mode || 'Selected'})
                          </span>
                        </div>

                        {/* Engagement stats row */}
                        <div className="flex flex-wrap gap-2 mt-3">
                          {hasTracking ? (
                            <>
                              <StatPill icon={CheckSquare} label="Delivered" value={newsletter?.delivery_count ?? 0} color="success" rate={deliveryRate} />
                              <StatPill icon={Eye} label="Opens" value={newsletter?.open_count ?? 0} color="primary" rate={openRate} />
                              <StatPill icon={MousePointer} label="Clicks" value={newsletter?.click_count ?? 0} color="accent" rate={clickRate} />
                              <StatPill icon={AlertTriangle} label="Bounces" value={newsletter?.bounce_count ?? 0} color="destructive" rate={bounceRate} />
                              <StatPill icon={ThumbsDown} label="Complaints" value={newsletter?.complaint_count ?? 0} color="warning" rate={complaintRate} />
                            </>
                          ) : (
                            <span className="text-xs text-muted-foreground italic">Tracking not available for this newsletter</span>
                          )}
                        </div>
                      </div>

                      <div className="flex items-center space-x-2 shrink-0 mt-1">
                        <button
                          onClick={() => copyFromHistory(newsletter)}
                          title="Copy to composer"
                          className="flex items-center space-x-1.5 px-3 py-1.5 bg-primary/10 text-primary hover:bg-primary/20 rounded-lg text-xs font-medium transition-colors"
                        >
                          <Copy className="w-3.5 h-3.5" />
                          <span>Copy</span>
                        </button>
                        <button
                          onClick={() => setExpandedNewsletter(expandedNewsletter === newsletter?.id ? null : newsletter?.id)}
                          title="Preview"
                          className="p-1.5 text-muted-foreground hover:text-foreground transition-colors"
                        >
                          {expandedNewsletter === newsletter?.id ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                        </button>
                        <button
                          onClick={() => deleteFromHistory(newsletter?.id)}
                          title="Delete"
                          className="p-1.5 text-muted-foreground hover:text-destructive transition-colors"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>

                    {/* Expanded preview */}
                    {expandedNewsletter === newsletter?.id && (
                      <div className="border-t border-border p-4">
                        {/* Preview tab switcher */}
                        <div className="flex space-x-1 bg-muted/30 rounded-lg p-1 w-fit mb-4">
                          <button
                            onClick={() => setExpandedPreviewTab(prev => ({ ...prev, [newsletter?.id]: 'preview' }))}
                            className={`px-3 py-1 rounded text-xs font-medium transition-all ${currentPreviewTab === 'preview' ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}
                          >
                            Rendered Preview
                          </button>
                          <button
                            onClick={() => setExpandedPreviewTab(prev => ({ ...prev, [newsletter?.id]: 'source' }))}
                            className={`px-3 py-1 rounded text-xs font-medium transition-all ${currentPreviewTab === 'source' ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}
                          >
                            HTML Source
                          </button>
                        </div>

                        {currentPreviewTab === 'preview' ? (
                          <div
                            className="border border-border rounded-lg p-4 bg-background prose prose-sm max-w-none text-sm overflow-auto max-h-72"
                            dangerouslySetInnerHTML={{ __html: newsletter?.html_body }}
                          />
                        ) : (
                          <pre className="text-xs text-foreground bg-muted/30 rounded-lg p-3 overflow-x-auto whitespace-pre-wrap font-mono max-h-64 overflow-y-auto">
                            {newsletter?.html_body}
                          </pre>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default NewsletterComposer;
